import React, { useState, useEffect, useCallback } from "react";
import { Col, Card, CardBody } from "reactstrap";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ORANGE_COLORS = {
  primary: '#FF5B00',
  dark: '#e85a28',
  darker: '#d14d1f',
  light: '#ff8c61',
  lighter: '#ffb399',
  subtle: '#fff5f0',
  muted: '#ffe8dd',
};

const cardShell = {
  background: "var(--vz-secondary-bg, #fff)",
  border: "1px solid var(--vz-border-color)",
  borderRadius: "10px",
  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  overflow: "hidden",
};

const toRgba = (hex, alpha) => {
  const value = hex.replace("#", "");
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const iconChip = (color) => ({
  width: "32px",
  height: "32px",
  borderRadius: "8px",
  border: `0.5px solid ${toRgba(color, 0.30)}`,
  background: toRgba(color, 0.10),
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
});

const iconGlyph = (color) => ({
  color,
  fontSize: 18,
});

const getAuthHeaders = () => {
  const authUser = JSON.parse(sessionStorage.getItem("authUser"));
  return {
    "Content-Type": "application/json",
    ...(authUser?.token && { Authorization: `Bearer ${authUser.token}` }),
  };
};

const MetricTile = ({ label, value, icon, color, borderColor, subtext, loading }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
      <Card
      className="card-animate"
      style={{
        ...cardShell,
        border: "1px solid var(--vz-border-color)",
        transition: 'all 0.3s ease',
        transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
        boxShadow: isHovered ? '0 8px 16px rgba(0,0,0,0.08)' : cardShell.boxShadow,
        cursor: 'pointer',
        minHeight: '140px',
        height: '100%'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <CardBody style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div className="d-flex align-items-center">
          <div className="flex-grow-1">
            <p className="text-uppercase fw-medium text-muted mb-0" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>
              {label}
            </p>
          </div>
          <div className="flex-shrink-0">
            <div style={iconChip(color)}>
              <div style={iconGlyph(color)}>{icon}</div>
            </div>
          </div>
        </div>
        <div className="d-flex align-items-end justify-content-between mt-auto pt-3">
          <div>
            {loading ? (
              <div style={{
                width: 60, height: 28, borderRadius: 4,
                background: 'linear-gradient(90deg, rgba(255,107,53,0.08) 25%, rgba(255,107,53,0.15) 50%, rgba(255,107,53,0.08) 75%)',
                backgroundSize: '200% 100%',
                animation: 'shimmer 1.4s infinite'
              }} />
            ) : (
              <h4 className="fs-22 fw-semibold ff-secondary mb-1" style={{ color: color }}>
                {value !== null && value !== undefined ? value : '0'}
              </h4>
            )}
            {subtext && !loading && (
              <p className="text-muted mb-0" style={{ fontSize: '12px' }}>
                {subtext}
              </p>
            )}
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

const EnhancedSeverityTiles = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/vulnerabilities/stats`, {
        headers: getAuthHeaders(),
      });

      if (response.status === 401) {
        sessionStorage.removeItem("authUser");
        window.location.href = "/login";
        return;
      }

      const data = await response.json();
      if (data?.success) {
        setStats(data.stats);
      } else {
        setError("Failed to load stats");
      }
    } catch (err) {
      console.error("Error fetching vulnerability stats:", err);
      setError("Failed to load stats");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const total = stats?.total_vulnerabilities ?? 0;
  const pct = (n) =>
    total > 0 ? `${((n / total) * 100).toFixed(0)}% of total` : '0% of total';

  const tileConfig = [
    {
      label: "Total Vulnerabilities",
      value: total.toLocaleString(),
      icon: <i className="ri-bug-line" />,
      color: ORANGE_COLORS.primary,
      borderColor: 'var(--vz-border-color)',
      subtext: stats?.new_24h > 0 ? `${stats.new_24h} new today` : 'No new today',
    },
    {
      label: "Critical",
      value: (stats?.critical ?? 0).toLocaleString(),
      icon: <i className="ri-error-warning-line" />,
      color: '#dc2626',
      borderColor: 'var(--vz-border-color)',
      subtext: pct(stats?.critical ?? 0),
    },
    {
      label: "High",
      value: (stats?.high ?? 0).toLocaleString(),
      icon: <i className="ri-alert-line" />,
      color: '#ea580c',
      borderColor: 'var(--vz-border-color)',
      subtext: pct(stats?.high ?? 0),
    },
    {
      label: "Medium",
      value: (stats?.medium ?? 0).toLocaleString(),
      icon: <i className="ri-information-line" />,
      color: '#d97706',
      borderColor: 'var(--vz-border-color)',
      subtext: pct(stats?.medium ?? 0),
    },
    {
      label: "Low",
      value: (stats?.low ?? 0).toLocaleString(),
      icon: <i className="ri-checkbox-circle-line" />,
      color: '#16a34a',
      borderColor: 'var(--vz-border-color)',
      subtext: pct(stats?.low ?? 0),
    },
    {
      label: "Affected Agents",
      value: (stats?.affected_agents ?? 0).toLocaleString(),
      icon: <i className="ri-computer-line" />,
      color: ORANGE_COLORS.dark,
      borderColor: 'var(--vz-border-color)',
      subtext: `${stats?.affected_packages ?? 0} packages affected`,
    },
    {
      label: "Avg CVSS Score",
      value: stats?.avg_cvss != null ? parseFloat(stats.avg_cvss).toFixed(1) : '0.0',
      icon: <i className="ri-line-chart-line" />,
      color: ORANGE_COLORS.light,
      borderColor: 'var(--vz-border-color)',
      subtext: `Max: ${stats?.max_cvss != null ? parseFloat(stats.max_cvss).toFixed(1) : 'N/A'}`,
    },
    {
      label: "Unique CVEs",
      value: (stats?.unique_cves ?? 0).toLocaleString(),
      icon: <i className="ri-shield-check-line" />,
      color: ORANGE_COLORS.lighter,
      borderColor: 'var(--vz-border-color)',
      subtext: `${stats?.resolved ?? 0} resolved`,
    },
  ];

  return (
    <>
      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>

      {error && (
        <Col xs={12} className="mb-2">
          <div className="alert alert-warning py-2 px-3" style={{ fontSize: '13px' }}>
            <i className="ri-wifi-off-line me-2" />
            {error} — showing zeros. <button className="btn btn-link btn-sm p-0 ms-1" onClick={fetchStats}>Retry</button>
          </div>
        </Col>
      )}

      {tileConfig.map((tile, idx) => (
        <Col xl={3} md={6} sm={6} xs={12} key={idx} className="mb-3">
          <MetricTile {...tile} loading={loading} />
        </Col>
      ))}
    </>
  );
};

export default EnhancedSeverityTiles;
